import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoanIntroComponent } from './loan-intro/loan-intro.component';
import { LoanHighlightsComponent } from './loan-highlights/loan-highlights.component';
import { LoanVerificationComponent } from './loan-verification/loan-verification.component';
import { LoanConsentsComponent } from './loan-consents/loan-consents.component';
import { RouterModule, Routes } from '@angular/router';
import { SharedDesktopModule } from '@brightside-web/shared/desktop';
import { LegalDocumentsService, SharedLegalDocumentsModule } from '@brightside-web/shared/legal-documents';
import { BrightsideUiModule } from '@brightside/brightside-ui';
import { TranslateModule } from '@ngx-translate/core';
import { MicroUiMoleculeModule } from '@micro-ui/molecule';
import {
  AccountCreationSteps,
  FinancialAccountCreationGuard,
  FinancialAccountsCoreService,
  FinancialAccountsCreationService,
  FinancialAccountsHistoryService,
  FinancialCoreKYCComponent,
  FinancialCoreKYCVerificationInProgressComponent,
} from '@brightside-web/desktop/feature/financial-accounts-core';
import { LoansMainComponent } from './loans-main/loans-main.component';
import { NewLoansService } from './new-loans.service';
import { LoanInfoGuard } from './loan-info-guard.service';
import { LoanInfoComponent } from './loan-info/loan-info.component';
import { AutosaveSettingsComponent, AutosaveSimpleSelectorComponent } from '@brightside-web/desktop/feature/autosave';
import { CanCreateLoanGuard } from './can-create-loan.guard';
import { LoansLegalDocsComponent } from './loans-legal-docs/loans-legal-docs.component';
import { LoanHasLegalGuard } from './loan-has-legal.guard';
import { NewSavingsAccountService } from '@brightside-web/desktop/feature/savings-account';
import { LoanDashboardComponent } from './loan-dashboard/loan-dashboard.component';
import { SharedAccountModule } from '@brightside-web/shared/account';
import { CanGoToDashboardGuard } from './can-go-to-dashboard.guard';
import { LoanDashboardOverviewComponent } from './loan-dashboard/loan-dashboard-overview/loan-dashboard-overview.component';
import { LoanDashboardDisclaimerComponent } from './loan-dashboard/loan-dashboard-disclaimer/loan-dashboard-disclaimer.component';
import { LoanDashboardContactDetailsComponent } from './loan-dashboard/loan-dashboard-contact-details/loan-dashboard-contact-details.component';

const ROUTES: Routes = [
  {
    path: '',
    component: LoansMainComponent,
    children: [
      {
        path: 'info',
        component: LoanInfoComponent,
        canActivate: [LoanInfoGuard],
      },
      {
        path: 'legal',
        component: LoansLegalDocsComponent,
      },
      {
        path: 'creation',
        canActivateChild: [CanCreateLoanGuard],
        children: [
          {
            path: AccountCreationSteps.INTRO,
            component: LoanIntroComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.INTRO },
          },
          {
            path: 'highlights',
            component: LoanHighlightsComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.INTRO },
          },
          {
            path: AccountCreationSteps.VERIFICATION,
            component: LoanVerificationComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.VERIFICATION },
          },
          {
            path: AccountCreationSteps.CONSENTS,
            component: LoanConsentsComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { creationStep: AccountCreationSteps.CONSENTS },
          },
          {
            path: AccountCreationSteps.KYC,
            component: FinancialCoreKYCComponent,
            canActivate: [FinancialAccountCreationGuard],
            data: { accountType: 'loans', creationStep: AccountCreationSteps.KYC },
          },
          {
            path: AccountCreationSteps.KYC_VERIFICATION,
            component: FinancialCoreKYCVerificationInProgressComponent,
            data: { accountType: 'loans' },
          },
          {
            path: 'autosave',
            component: AutosaveSettingsComponent,
            data: {
              routeOnSuccess: ['accounts/loans/info'],
              routeOnBack: ['accounts/loans/info'],
              creationStep: AccountCreationSteps.COMPLETED,
            },
          },
          {
            path: 'autosave-select',
            component: AutosaveSimpleSelectorComponent,
            data: {
              routeOnSuccess: ['accounts/loans/info'],
              routeOnBack: ['accounts/loans/info'],
              creationStep: AccountCreationSteps.COMPLETED,
            },
          },
        ],
      },
      {
        path: 'dashboard',
        component: LoanDashboardComponent,
        canActivate: [CanGoToDashboardGuard],
      },
      {
        path: '**',
        redirectTo: 'info',
      },
    ],
  },
];

@NgModule({
  imports: [
    CommonModule,
    RouterModule.forChild(ROUTES),
    SharedDesktopModule,
    SharedLegalDocumentsModule,
    SharedAccountModule,
    BrightsideUiModule,
    TranslateModule,
    MicroUiMoleculeModule,
  ],
  declarations: [
    LoanIntroComponent,
    LoanHighlightsComponent,
    LoanVerificationComponent,
    LoanConsentsComponent,
    LoansMainComponent,
    LoanInfoComponent,
    LoansLegalDocsComponent,
    LoanDashboardComponent,
    LoanDashboardOverviewComponent,
    LoanDashboardDisclaimerComponent,
    LoanDashboardContactDetailsComponent,
  ],
  providers: [
    FinancialAccountsCoreService,
    FinancialAccountsCreationService,
    FinancialAccountCreationGuard,
    NewSavingsAccountService,
    NewLoansService,
    FinancialAccountsHistoryService,
    LoanInfoGuard,
    CanCreateLoanGuard,
    LegalDocumentsService,
    LoanHasLegalGuard,
    CanGoToDashboardGuard,
  ],
})
export class DesktopFeatureLoansModule {}
